import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Wallet,
  Plus,
  Search,
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Filter,
  Calculator,
  Upload,
  FileUp,
  X,
  CheckCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

const categorias = [
  'Veículos',
  'Administrativo',
  'Combustível',
  'Manutenção',
  'Multas',
  'Impostos',
  'Seguros',
  'Documentação',
  'Marketing',
  'Outros'
];

const formasPagamento = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX' },
  { value: 'cartao_credito', label: 'Cartão de Crédito' },
  { value: 'cartao_debito', label: 'Cartão de Débito' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'transferencia', label: 'Transferência' }
];

const referencias = [
  { value: 'veiculos', label: 'Veículos' },
  { value: 'administrativo', label: 'Administrativo' },
  { value: 'outros', label: 'Outros' }
];

export default function FinanceiroPage() {
  const { API } = useAuth();
  
  // Obter data atual no formato correto
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // JavaScript months are 0-indexed
  const firstDayOfMonth = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-01`;
  const currentMonthFilter = `${currentYear}-${currentMonth.toString().padStart(2, '0')}`;
  
  const [transactions, setTransactions] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters - Inicializar com mês atual
  const [tipoFilter, setTipoFilter] = useState('todos');
  const [referenciaFilter, setReferenciaFilter] = useState('todos');
  const [categoriaFilter, setCategoriaFilter] = useState('todas');
  const [mesFilter, setMesFilter] = useState(currentMonthFilter); // Formato: YYYY-MM, iniciado com mês atual
  const [dataInicio, setDataInicio] = useState(firstDayOfMonth); // Sempre dia 1 do mês atual
  const [dataFim, setDataFim] = useState('');
  const [search, setSearch] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [formMode, setFormMode] = useState('create');
  const [transactionType, setTransactionType] = useState('despesa');
  
  // Import CSV states
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [substituir, setSubstituir] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Calculator
  const [percentual, setPercentual] = useState(50);
  
  // ✅ CALCULAR STATS LOCALMENTE A PARTIR DAS TRANSAÇÕES FILTRADAS
  // Isso garante que os cards sempre mostram o que está na lista
  const stats = React.useMemo(() => {
    const total_receitas = transactions
      .filter(t => t.tipo === 'receita')
      .reduce((sum, t) => sum + t.valor, 0);
    
    const total_despesas = transactions
      .filter(t => t.tipo === 'despesa')
      .reduce((sum, t) => sum + t.valor, 0);
    
    const saldo = total_receitas - total_despesas;
    
    return { total_receitas, total_despesas, saldo };
  }, [transactions]);
  
  // Form state
  const [formData, setFormData] = useState({
    tipo: 'despesa',
    data: new Date().toISOString().split('T')[0],
    referencia: 'veiculos',
    vehicle_id: '',
    descricao: '',
    categoria: 'Veículos',
    forma_pagamento: 'pix',
    valor: 0,
    observacoes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadTransactions();
    loadVehicles();
  }, [tipoFilter, referenciaFilter, categoriaFilter, mesFilter, dataInicio, dataFim]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (tipoFilter !== 'todos') params.append('tipo', tipoFilter);
      if (referenciaFilter !== 'todos') params.append('referencia', referenciaFilter);
      if (categoriaFilter !== 'todas') params.append('categoria', categoriaFilter);
      
      // Filtro por mês tem prioridade sobre datas individuais
      if (mesFilter) {
        const [ano, mes] = mesFilter.split('-');
        const primeiroDia = `${ano}-${mes}-01`;
        // Calcular o último dia do mês corretamente
        // new Date(year, month, 0) retorna o último dia do mês anterior
        // Como mes vem como string "01"-"12", ao usar new Date precisamos do próximo mês
        // Exemplo: mes="02" → parseInt("02")=2 → new Date(2026, 2, 0) = último dia do mês 1 (janeiro)
        // Correto: new Date(2026, parseInt("02"), 0) = último dia de fevereiro
        const ultimoDia = new Date(parseInt(ano), parseInt(mes), 0).getDate();
        const ultimoDiaFormatado = `${ano}-${mes}-${ultimoDia.toString().padStart(2, '0')}`;
        params.append('data_inicio', primeiroDia);
        params.append('data_fim', ultimoDiaFormatado);
      } else {
        if (dataInicio) params.append('data_inicio', dataInicio);
        if (dataFim) params.append('data_fim', dataFim);
      }
      
      if (search) params.append('search', search);
      
      console.log('Loading transactions with params:', params.toString());
      const response = await axios.get(`${API}/transactions?${params}`, { withCredentials: true });
      console.log('Transactions loaded:', response.data.length);
      setTransactions(response.data);
    } catch (error) {
      console.error('Error loading transactions:', error);
      toast.error('Erro ao carregar transações. Por favor, recarregue a página.');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`, { withCredentials: true });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.descricao.trim()) {
      toast.error('Informe a descrição da transação');
      return;
    }
    
    if (formData.valor <= 0) {
      toast.error('O valor deve ser maior que zero');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const dataToSend = {
        ...formData,
        tipo: transactionType,
        vehicle_id: formData.referencia === 'veiculos' && formData.vehicle_id && formData.vehicle_id !== 'none' ? formData.vehicle_id : null
      };
      
      if (formMode === 'create') {
        await axios.post(`${API}/transactions`, dataToSend, { withCredentials: true });
        toast.success(`${transactionType === 'receita' ? 'Receita' : 'Despesa'} registrada com sucesso!`);
      } else {
        await axios.put(`${API}/transactions/${selectedTransaction.transaction_id}`, dataToSend, { withCredentials: true });
        toast.success('Transação atualizada com sucesso!');
      }
      setShowModal(false);
      resetForm();
      loadTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar transação');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/transactions/${selectedTransaction.transaction_id}`, { withCredentials: true });
      toast.success('Transação excluída com sucesso!');
      setShowDeleteDialog(false);
      loadTransactions();
    } catch (error) {
      toast.error('Erro ao excluir transação');
    }
  };

  const resetForm = () => {
    setFormData({
      tipo: 'despesa',
      data: new Date().toISOString().split('T')[0],
      referencia: 'veiculos',
      vehicle_id: '',
      descricao: '',
      categoria: 'Veículos',
      forma_pagamento: 'pix',
      valor: 0,
      observacoes: ''
    });
    setSelectedTransaction(null);
  };

  const openCreateModal = (tipo) => {
    setFormMode('create');
    setTransactionType(tipo);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (transaction) => {
    setFormMode('edit');
    setTransactionType(transaction.tipo);
    setSelectedTransaction(transaction);
    setFormData({
      tipo: transaction.tipo,
      data: transaction.data,
      referencia: transaction.referencia,
      vehicle_id: transaction.vehicle_id || '',
      descricao: transaction.descricao,
      categoria: transaction.categoria,
      forma_pagamento: transaction.forma_pagamento,
      valor: transaction.valor,
      observacoes: transaction.observacoes || ''
    });
    setShowModal(true);
  };

  const openDeleteDialog = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDeleteDialog(true);
  };

  const handleImportCSV = async () => {
    if (!csvFile) {
      toast.error('Selecione um arquivo CSV');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await axios.post(
        `${API}/import/csv-transactions?substituir=${substituir}`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setImportResult(response.data);
      
      if (response.data.sucesso) {
        toast.success(response.data.mensagem);
        loadTransactions();
      } else {
        toast.warning(response.data.mensagem);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo CSV');
      setImportResult({
        sucesso: false,
        erros: [error.response?.data?.detail || 'Erro ao importar arquivo']
      });
    } finally {
      setImporting(false);
    }
  };

  const resetImportModal = () => {
    setCsvFile(null);
    setSubstituir(false);
    setImportResult(null);
    setShowImportModal(false);
  };

  const clearFilters = () => {
    // Resetar para mês atual
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const firstDay = `${year}-${month.toString().padStart(2, '0')}-01`;
    const monthFilter = `${year}-${month.toString().padStart(2, '0')}`;
    
    setTipoFilter('todos');
    setReferenciaFilter('todos');
    setCategoriaFilter('todas');
    setMesFilter(monthFilter);
    setDataInicio(firstDay);
    setDataFim('');
    setSearch('');
    // Forçar reload imediato
    setTimeout(() => {
      loadTransactions();
    }, 100);
    toast.success('Filtros limpos! Exibindo mês atual.');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Máscara para valor monetário (1.234,56)
  const maskCurrency = (value) => {
    // Remove tudo que não é número
    let v = String(value).replace(/\D/g, '');
    // Converte para centavos
    v = (parseInt(v) / 100).toFixed(2);
    // Formata com ponto e vírgula brasileiro
    return v.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const parseCurrency = (value) => {
    if (!value) return 0;
    // Remove pontos e troca vírgula por ponto
    const cleaned = String(value).replace(/\./g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  };

  const handleValorChange = (e) => {
    const value = e.target.value;
    const formatted = maskCurrency(value);
    setFormData({ ...formData, valor: parseCurrency(formatted), valorDisplay: formatted });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const getFormaPagamentoLabel = (value) => {
    const forma = formasPagamento.find(f => f.value === value);
    return forma ? forma.label : value;
  };

  // Filter transactions by search
  const filteredTransactions = transactions.filter(t => {
    if (!search) return true;
    const s = search.toLowerCase();
    return t.descricao?.toLowerCase().includes(s) ||
           t.veiculo_info?.toLowerCase().includes(s) ||
           t.vehicle_id?.toLowerCase().includes(s) ||
           t.categoria?.toLowerCase().includes(s) ||
           t.transaction_id?.toLowerCase().includes(s);
  });

  // Calculate value based on percentage
  const valorCalculado = (stats.saldo * percentual) / 100;
  
  // Count active filters
  const activeFiltersCount = [
    tipoFilter !== 'todos',
    referenciaFilter !== 'todos',
    categoriaFilter !== 'todas',
    mesFilter !== '',
    dataInicio !== '',
    dataFim !== '',
    search !== ''
  ].filter(Boolean).length;

  return (
    <div className="space-y-6" data-testid="financeiro-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <Wallet className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-[Manrope] text-gray-900">Gestão Financeira</h1>
            <p className="text-gray-500 text-sm">Controle suas entradas e saídas</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={() => {
              setLoading(true);
              loadTransactions();
            }} 
            variant="outline"
            className="border-gray-300"
            title="Recarregar dados"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Recarregar
          </Button>
          <Button 
            onClick={() => openCreateModal('despesa')} 
            className="bg-red-500 hover:bg-red-600"
            data-testid="add-despesa-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Despesa
          </Button>
          <Button 
            onClick={() => openCreateModal('receita')} 
            className="bg-green-500 hover:bg-green-600"
            data-testid="add-receita-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Receita
          </Button>
          <Button 
            onClick={() => setShowImportModal(true)} 
            variant="outline"
            className="border-blue-500 text-blue-500 hover:bg-blue-50"
            data-testid="import-csv-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importar CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filtros de Movimentação
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-8 gap-4">
            <div>
              <Label className="text-xs text-gray-500">Referência</Label>
              <Select value={referenciaFilter} onValueChange={setReferenciaFilter}>
                <SelectTrigger data-testid="referencia-filter">
                  <SelectValue placeholder="Todas as referências" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas as referências</SelectItem>
                  {referencias.map((ref) => (
                    <SelectItem key={ref.value} value={ref.value}>{ref.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Filtro de Mês - Melhorado */}
            <div className="md:col-span-2">
              <Label className="text-xs text-gray-500">Filtro Rápido por Mês</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select 
                  value={mesFilter ? mesFilter.split('-')[1] : ''} 
                  onValueChange={(mes) => {
                    const ano = mesFilter ? mesFilter.split('-')[0] : currentYear.toString();
                    const novoMesFilter = `${ano}-${mes}`;
                    setMesFilter(novoMesFilter);
                    setDataInicio(`${novoMesFilter}-01`);
                    setDataFim('');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o mês" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="01">Janeiro</SelectItem>
                    <SelectItem value="02">Fevereiro</SelectItem>
                    <SelectItem value="03">Março</SelectItem>
                    <SelectItem value="04">Abril</SelectItem>
                    <SelectItem value="05">Maio</SelectItem>
                    <SelectItem value="06">Junho</SelectItem>
                    <SelectItem value="07">Julho</SelectItem>
                    <SelectItem value="08">Agosto</SelectItem>
                    <SelectItem value="09">Setembro</SelectItem>
                    <SelectItem value="10">Outubro</SelectItem>
                    <SelectItem value="11">Novembro</SelectItem>
                    <SelectItem value="12">Dezembro</SelectItem>
                  </SelectContent>
                </Select>
                <Select 
                  value={mesFilter ? mesFilter.split('-')[0] : ''} 
                  onValueChange={(ano) => {
                    const mes = mesFilter ? mesFilter.split('-')[1] : currentMonth.toString().padStart(2, '0');
                    const novoMesFilter = `${ano}-${mes}`;
                    setMesFilter(novoMesFilter);
                    setDataInicio(`${novoMesFilter}-01`);
                    setDataFim('');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Ano" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Data Inicial</Label>
              <Input
                type="date"
                value={dataInicio}
                onChange={(e) => {
                  setDataInicio(e.target.value);
                  // Limpar mês ao selecionar data individual
                  if (e.target.value) {
                    setMesFilter('');
                  }
                }}
                data-testid="filter-data-inicio"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Data Final</Label>
              <Input
                type="date"
                value={dataFim}
                onChange={(e) => {
                  setDataFim(e.target.value);
                  // Limpar mês ao selecionar data individual
                  if (e.target.value) {
                    setMesFilter('');
                  }
                }}
                data-testid="filter-data-fim"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-gray-500">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por descrição ou veículo..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-transactions"
                />
              </div>
            </div>
            <div className="flex items-end">
              <Button
                onClick={clearFilters}
                variant="outline"
                className="w-full border-2 border-red-500 text-red-600 hover:bg-red-50 hover:border-red-600 font-semibold"
                data-testid="clear-filters-button"
              >
                <X className="w-5 h-5 mr-2" />
                Limpar Todos os Filtros
              </Button>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Categoria</Label>
              <Select value={categoriaFilter} onValueChange={setCategoriaFilter}>
                <SelectTrigger data-testid="categoria-filter">
                  <SelectValue placeholder="Todas as categorias" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as categorias</SelectItem>
                  {categorias.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={clearFilters} className="w-full">
                Limpar
              </Button>
            </div>
          </div>
          
          {/* Type filter buttons */}
          <div className="flex gap-2 mt-4">
            <Button 
              variant={tipoFilter === 'todos' ? 'default' : 'outline'}
              onClick={() => setTipoFilter('todos')}
              size="sm"
            >
              Todos
            </Button>
            <Button 
              variant={tipoFilter === 'receita' ? 'default' : 'outline'}
              onClick={() => setTipoFilter('receita')}
              size="sm"
              className={tipoFilter === 'receita' ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              Receitas
            </Button>
            <Button 
              variant={tipoFilter === 'despesa' ? 'default' : 'outline'}
              onClick={() => setTipoFilter('despesa')}
              size="sm"
              className={tipoFilter === 'despesa' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              Despesas
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Active Filters Alert */}
      {activeFiltersCount > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <p className="text-sm text-yellow-800">
              <strong>{activeFiltersCount} filtro(s) ativo(s):</strong> 
              {mesFilter && ` Mês: ${(() => {
        const [ano, mes] = mesFilter.split('-');
        const meses = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 
                       'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
        return `${meses[parseInt(mes) - 1]} de ${ano}`;
      })()}`}
              {!mesFilter && dataInicio && ` Data Inicial: ${new Date(dataInicio).toLocaleDateString('pt-BR')}`}
              {!mesFilter && dataFim && ` • Data Final: ${new Date(dataFim).toLocaleDateString('pt-BR')}`}
              {tipoFilter !== 'todos' && ` • Tipo: ${tipoFilter}`}
              {referenciaFilter !== 'todos' && ` • Referência: ${referenciaFilter}`}
              {categoriaFilter !== 'todas' && ` • Categoria: ${categoriaFilter}`}
              {search && ` • Busca: "${search}"`}
            </p>
          </div>
          <Button
            onClick={clearFilters}
            variant="outline"
            size="sm"
            className="border-yellow-600 text-yellow-700 hover:bg-yellow-100"
          >
            <X className="w-4 h-4 mr-1" />
            Limpar
          </Button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Total de Entradas</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(stats.total_receitas)}</p>
              </div>
              <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Total de Saídas</p>
                <p className="text-2xl font-bold text-red-700">{formatCurrency(stats.total_despesas)}</p>
              </div>
              <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`bg-gradient-to-br ${stats.saldo >= 0 ? 'from-blue-50 to-blue-100 border-blue-200' : 'from-orange-50 to-orange-100 border-orange-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${stats.saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Saldo</p>
                <p className={`text-2xl font-bold ${stats.saldo >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>{formatCurrency(stats.saldo)}</p>
              </div>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${stats.saldo >= 0 ? 'bg-blue-200' : 'bg-orange-200'}`}>
                <DollarSign className={`w-5 h-5 ${stats.saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Percentage Calculator */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            Calculadora de Percentual
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <Label>Percentual (%)</Label>
              <Input
                type="number"
                placeholder="Ex: 30"
                value={percentual}
                onChange={(e) => setPercentual(parseFloat(e.target.value) || 0)}
                min="0"
                max="100"
              />
            </div>
            <div className="flex-1">
              <Label>Saldo Atual</Label>
              <Input value={formatCurrency(stats.saldo)} disabled />
            </div>
            <div className="flex-1">
              <Label>Valor Calculado</Label>
              <Input 
                value={formatCurrency(valorCalculado)} 
                disabled 
                className="font-bold"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Wallet className="w-12 h-12 mb-2 opacity-50" />
              <p>Nenhuma transação encontrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Referência</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Pagamento</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map((transaction) => (
                    <TableRow key={transaction.transaction_id} data-testid={`transaction-row-${transaction.transaction_id}`}>
                      <TableCell>
                        <Badge className={transaction.tipo === 'receita' ? 'bg-green-500' : 'bg-red-500'}>
                          {transaction.tipo === 'receita' ? 'RECEITA' : 'DESPESA'}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(transaction.data)}</TableCell>
                      <TableCell>
                        <div>
                          <span className="text-xs text-gray-500 uppercase">{transaction.referencia}</span>
                          {transaction.veiculo_info && (
                            <p className="text-sm font-medium">{transaction.vehicle_id}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="max-w-xs truncate" title={transaction.descricao}>
                          {transaction.descricao}
                        </p>
                        {transaction.veiculo_info && (
                          <p className="text-xs text-gray-500">{transaction.veiculo_info}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{transaction.categoria}</Badge>
                      </TableCell>
                      <TableCell>{getFormaPagamentoLabel(transaction.forma_pagamento)}</TableCell>
                      <TableCell className={`text-right font-semibold ${transaction.tipo === 'receita' ? 'text-green-600' : 'text-red-600'}`}>
                        {transaction.tipo === 'receita' ? '+' : '-'} {formatCurrency(transaction.valor)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditModal(transaction)}
                            data-testid={`edit-transaction-${transaction.transaction_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteDialog(transaction)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-transaction-${transaction.transaction_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className={transactionType === 'receita' ? 'text-green-600' : 'text-red-600'}>
              {formMode === 'create' 
                ? (transactionType === 'receita' ? 'Adicionar Receita' : 'Adicionar Despesa')
                : 'Editar Transação'
              }
            </DialogTitle>
            <p className="text-sm text-gray-500">Preencha os detalhes da movimentação financeira.</p>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Item/Referência - Radio Buttons */}
            <div>
              <Label className="text-sm font-medium">Item/Referência *</Label>
              <div className="flex gap-6 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="referencia" 
                    value="administrativo"
                    checked={formData.referencia === 'administrativo'}
                    onChange={(e) => setFormData({ ...formData, referencia: e.target.value, vehicle_id: '' })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Administrativo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="referencia" 
                    value="veiculos"
                    checked={formData.referencia === 'veiculos'}
                    onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Veículos</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="referencia" 
                    value="outros"
                    checked={formData.referencia === 'outros'}
                    onChange={(e) => setFormData({ ...formData, referencia: e.target.value, vehicle_id: '' })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Outros</span>
                </label>
              </div>
            </div>

            {/* Veículo (Opcional) */}
            <div>
              <Label htmlFor="vehicle_id">Veículo (Opcional)</Label>
              <Select 
                value={formData.vehicle_id || 'none'} 
                onValueChange={(value) => setFormData({ ...formData, vehicle_id: value === 'none' ? '' : value })}
                disabled={formData.referencia !== 'veiculos'}
              >
                <SelectTrigger data-testid="form-vehicle" className={formData.referencia !== 'veiculos' ? 'opacity-50' : ''}>
                  <SelectValue placeholder="Selecione um veículo (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Selecione um veículo (opcional)</SelectItem>
                  {vehicles.map((v) => (
                    <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                      {v.vehicle_id} - {v.marca} {v.modelo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Descrição */}
            <div>
              <Label htmlFor="descricao">Descrição *</Label>
              <Input
                id="descricao"
                placeholder="Ex: Referente ao mês 01/2025"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                data-testid="form-descricao"
              />
            </div>

            {/* Valor e Data */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="valor">Valor (R$) *</Label>
                <Input
                  id="valor"
                  type="text"
                  placeholder="0,00"
                  value={formData.valorDisplay || (formData.valor ? maskCurrency(Math.round(formData.valor * 100)) : '')}
                  onChange={handleValorChange}
                  data-testid="form-valor"
                />
              </div>
              <div>
                <Label htmlFor="data">Data *</Label>
                <Input
                  id="data"
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                  data-testid="form-data"
                />
              </div>
            </div>

            {/* Categoria - Radio Buttons */}
            <div>
              <Label className="text-sm font-medium">Categoria *</Label>
              <div className="flex gap-6 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="categoria" 
                    value="Administrativo"
                    checked={formData.categoria === 'Administrativo'}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Administrativo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="categoria" 
                    value="Veículos"
                    checked={formData.categoria === 'Veículos'}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Veículos</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="categoria" 
                    value="Outros"
                    checked={formData.categoria === 'Outros'}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Outros</span>
                </label>
              </div>
            </div>

            {/* Forma de Pagamento - Radio Grid */}
            <div>
              <Label className="text-sm font-medium">Forma de Pagamento *</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="forma_pagamento" 
                    value="pix"
                    checked={formData.forma_pagamento === 'pix'}
                    onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">PIX</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="forma_pagamento" 
                    value="cartao_debito"
                    checked={formData.forma_pagamento === 'cartao_debito'}
                    onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Débito</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="forma_pagamento" 
                    value="dinheiro"
                    checked={formData.forma_pagamento === 'dinheiro'}
                    onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Dinheiro</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="forma_pagamento" 
                    value="cartao_credito"
                    checked={formData.forma_pagamento === 'cartao_credito'}
                    onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Crédito</span>
                </label>
              </div>
            </div>

            {/* Observações */}
            <div>
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                placeholder="Observações adicionais..."
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                rows={3}
                data-testid="form-observacoes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className={transactionType === 'receita' ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'}
                data-testid="save-transaction-button"
              >
                {submitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import CSV Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-500" />
              Importar Transações via CSV
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* File Upload */}
            {!importResult && (
              <>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 transition-colors">
                  <div className="flex flex-col items-center gap-3">
                    <FileUp className="w-12 h-12 text-gray-400" />
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-700">
                        Selecione um arquivo CSV
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        O arquivo deve conter as colunas: tipo, valor, data, descricao
                      </p>
                    </div>
                    <Input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setCsvFile(e.target.files[0])}
                      className="max-w-xs"
                    />
                    {csvFile && (
                      <div className="flex items-center gap-2 text-sm text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        {csvFile.name}
                      </div>
                    )}
                  </div>
                </div>

                {/* Options */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <Label className="text-sm font-medium text-gray-700">Opções de Importação</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="substituir"
                      checked={substituir}
                      onChange={(e) => setSubstituir(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor="substituir" className="text-sm text-gray-600 cursor-pointer">
                      Substituir transações existentes (mesmo ID)
                    </Label>
                  </div>
                </div>

                {/* Format Guide */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Formato do CSV</h4>
                  <div className="text-xs text-blue-800 space-y-1">
                    <p><strong>Campos obrigatórios:</strong> tipo, valor, data, descricao</p>
                    <p><strong>Campos opcionais:</strong> veiculo_id, veiculo_info, categoria, forma_pagamento, observacoes</p>
                    <p><strong>Formato da data:</strong> AAAA-MM-DD (ex: 2026-02-09)</p>
                    <p><strong>Tipo:</strong> receita ou despesa</p>
                  </div>
                </div>
              </>
            )}

            {/* Import Result */}
            {importResult && (
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${importResult.sucesso ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <div className="flex items-start gap-3">
                    {importResult.sucesso ? (
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h4 className={`font-medium ${importResult.sucesso ? 'text-green-900' : 'text-yellow-900'}`}>
                        {importResult.mensagem || 'Importação concluída'}
                      </h4>
                      <div className="mt-2 text-sm space-y-1">
                        <p className="text-gray-700">
                          <strong>Total de registros:</strong> {importResult.total_registros}
                        </p>
                        <p className="text-green-700">
                          <strong>Importados:</strong> {importResult.importados}
                        </p>
                        <p className="text-yellow-700">
                          <strong>Ignorados:</strong> {importResult.ignorados}
                        </p>
                        <p className="text-red-700">
                          <strong>Erros:</strong> {importResult.erros?.length || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Errors and Details */}
                {importResult.erros && importResult.erros.length > 0 && (
                  <div className="bg-red-50 p-4 rounded-lg max-h-60 overflow-y-auto">
                    <h4 className="text-sm font-medium text-red-900 mb-2">Erros Encontrados:</h4>
                    <ul className="text-xs text-red-800 space-y-1">
                      {importResult.erros.slice(0, 10).map((erro, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-red-500">•</span>
                          <span>{erro}</span>
                        </li>
                      ))}
                      {importResult.erros.length > 10 && (
                        <li className="text-red-600 font-medium">
                          ... e mais {importResult.erros.length - 10} erros
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {/* Details */}
                {importResult.detalhes && importResult.detalhes.length > 0 && (
                  <div className="bg-yellow-50 p-4 rounded-lg max-h-40 overflow-y-auto">
                    <h4 className="text-sm font-medium text-yellow-900 mb-2">Avisos:</h4>
                    <ul className="text-xs text-yellow-800 space-y-1">
                      {importResult.detalhes.map((detalhe, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-yellow-500">•</span>
                          <span>{detalhe}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            {!importResult ? (
              <>
                <Button
                  variant="outline"
                  onClick={resetImportModal}
                  disabled={importing}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleImportCSV}
                  disabled={!csvFile || importing}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  {importing ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Importando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Importar
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={resetImportModal} className="bg-blue-500 hover:bg-blue-600">
                Fechar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Transação</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta {selectedTransaction?.tipo === 'receita' ? 'receita' : 'despesa'}?
              <br />
              <strong>{selectedTransaction?.descricao}</strong>
              <br />
              Valor: {formatCurrency(selectedTransaction?.valor)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-button"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
